import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { apiRoutes } from '@core/constants/api-routes.constants';
import { map, Observable, of } from 'rxjs';
import { LookupListCategory, LookupListItem } from './models/admin/lookupList.model';
import { PageModel } from './models/type/type';
import { RecordTypeNumericEnum } from '@shared/enums/records.enum';
import { IStandard } from '@shared/models/standards/standard';
import { IScholarshipRolloverResult, IScholarshipRolloverStatus } from './models/admin/scholarship-rollover.model';
import { StandardType } from './models/standard-types/standard-types';
import { Lookup } from './models/shared/lookup';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  constructor(private readonly http: HttpClient) {}

  private lookupLists: { [category: number]: LookupListItem[] } = {};

  public getLookupList = (category: LookupListCategory, activeOnly: boolean = false): Observable<LookupListItem[]> => {
    if (this.lookupLists[category]?.length) {
      const list = this.lookupLists[category].filter(i => !activeOnly || !i.disabled);
      return of(list);
    } else {
      return this.http.get<LookupListItem[]>(apiRoutes.lookup.getLookupList(category)).pipe(
        map(list => {
          this.lookupLists[category] = list;
          return list.filter(i => !activeOnly || !i.disabled);
        })
      );
    }
  };

  public getAllLookupLists = (): Observable<LookupListItem[]> => {
    return this.http.get<LookupListItem[]>(apiRoutes.admin.getLookupLists());
  };

  public saveLookupListItems = (listItems: LookupListItem[]): Observable<LookupListItem[]> => {
    return this.http.patch<LookupListItem[]>(apiRoutes.admin.saveLookupLists(), listItems);
  };

  public clearCache = () => {
    this.lookupLists = {};
  };

  public getTypes = (recordType: RecordTypeNumericEnum): Observable<PageModel[]> => {
    return this.http.get<PageModel[]>(apiRoutes.admin.getTypes(recordType));
  };

  getRolloverStandards(): Observable<IStandard[]> {
    return this.http.get<IStandard[]>(apiRoutes.admin.getRolloverStandards());
  }

  public rolloverScholarships = (newGradingSchemeName: string): Observable<IScholarshipRolloverResult> => {
    return this.http.post<IScholarshipRolloverResult>(apiRoutes.admin.rolloverScholarships(), {
      newGradingSchemeName
    });
  };

  public getScholarshipRolloverStatus = (): Observable<IScholarshipRolloverStatus> => {
    return this.http.get<IScholarshipRolloverStatus>(apiRoutes.admin.getScholarshipRolloverStatus());
  };

  public getStandardTypes(): Observable<StandardType[]> {
    return this.http.get<StandardType[]>(apiRoutes.admin.getStandardTypes());
  }

  public createStandardType(data: StandardType) {
    return this.http.post(apiRoutes.admin.createStandardType(), data);
  }

  public updateStandardType(id: string, data: StandardType) {
    return this.http.put(apiRoutes.admin.updateStandardType(id), data);
  }

  mapStandardTypesOption(standardTypes: StandardType[]): Lookup<string>[] {
    return standardTypes
      .filter((standardType): standardType is typeof standardType & { id: string } => standardType.id != null)
      .map(standardType => ({
        value: standardType.id,
        text: standardType.title
      }));
  }
}
